import React, { useState, Fragment } from 'react';
import { invoke } from 'lodash';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import apolloClient from '@src/graphql/apollo';
import { AddBraintreePaymentMethod } from '@src/containers/AddBraintreePaymentMethod';
import { SwitchTabs } from '@src/components/SwitchTabs';
import InvoiceDetails from '@src/containers/InvoiceDetails';
import { trackOpenAddPaymentMethod } from '@src/metrics';
import { MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { PaymentMethodTypeEnum, SetPaymentMethodTypeDocument } from '@src/graphql/generated';
import './styles.scss';

const SWITCH_TABS = {
  CREDITCARD: { label: 'Credit Card', methodType: PaymentMethodTypeEnum.CREDITCARD },
  INVOICE: { label: 'Invoice', methodType: PaymentMethodTypeEnum.INVOICE },
};

export default function AddPaymentMethod({
  disableInvoicing,
  className,
  buttonLabel,
  metricsContext,
  onMethodChanged,
  onPaymentMethodAdded,
}) {
  const [methodType, setMethodType] = useState<PaymentMethodTypeEnum>(SWITCH_TABS.CREDITCARD.methodType);

  async function handlePaymentMethodAdded() {
    await apolloClient.mutate({
      mutation: SetPaymentMethodTypeDocument,
      variables: { methodType: methodType.toUpperCase() },
    });
    invoke(window.top, 'fetchCustomerStatus');
    await onPaymentMethodAdded(methodType);
  }

  return (
    <div className={classnames('add-payment-method', className)}>
      <Fragment>
        <div className="add-payment-method__title">{`${SWITCH_TABS[methodType].label} details`}</div>
        {!disableInvoicing && (
          <div className="add-payment-method__switch">
            <SwitchTabs
              active={methodType}
              dataKey="methodType"
              list={Object.values(SWITCH_TABS)}
              onClick={({ methodType }) => {
                setMethodType(methodType);
                onMethodChanged && onMethodChanged(methodType);
                trackOpenAddPaymentMethod({
                  'Event Source': MetricEventSource.SWITCH_PAYMENT_METHOD_TAB,
                  'Previous Context': metricsContext,
                  'Type': methodType,
                });
              }}
            />
          </div>
        )}
      </Fragment>

      {methodType === SWITCH_TABS.INVOICE.methodType && (
        <p className="add-payment-method-invoice-info">
          Invoiced subscriptions do not incur any additional fee when business customers pay at least 3 months in
          advance. For monthly invoices, a service fee of 7 EUR per invoice is applied to cover the cost of processing
          the invoices.
        </p>
      )}

      {methodType === SWITCH_TABS.CREDITCARD.methodType ? (
        <AddBraintreePaymentMethod buttonLabel={buttonLabel} onPaymentMethodAdded={handlePaymentMethodAdded} />
      ) : (
        <InvoiceDetails buttonLabel={buttonLabel} checkTerms onUpdated={handlePaymentMethodAdded} />
      )}
    </div>
  );
}

AddPaymentMethod.propTypes = {
  buttonLabel: PropTypes.string,
  className: PropTypes.string,
  disableInvoicing: PropTypes.bool,
  metricsContext: PropTypes.oneOf(Object.values(MetricContext)).isRequired,
  onMethodChanged: PropTypes.func,
  onPaymentMethodAdded: PropTypes.func.isRequired,
};
